// Tooltip.jsx
import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import styles from "../../styles/Tooltip.module.css";
import { getOverlayRoot, getParentZIndex } from "../../utils/layerManager";

const Tooltip = ({
  children,
  content,
  position = "top",
  customClass = "",
  delay = 0,
  arrow = true,
  zIndexOffset = 10,
  tooltipStyle = {},
}) => {
  const [visible, setVisible] = useState(false);
  const [zIndex, setZIndex] = useState(null);
  const triggerRef = useRef(null);
  const tooltipRef = useRef(null);
  let timeout;

  const showTip = () => {
    timeout = setTimeout(() => {
      if (triggerRef.current) {
        // Calculate z-index from parent
        const parentZ = getParentZIndex(triggerRef.current);
        setZIndex(parentZ + zIndexOffset);
      }
      setVisible(true);
    }, delay);
  };

  const hideTip = () => {
    clearTimeout(timeout);
    setVisible(false);
  };

  // Update tooltip position when visible and position changes
  useEffect(() => {
    if (visible && triggerRef.current && tooltipRef.current) {
      const updatePosition = () => {
        if (!triggerRef.current || !tooltipRef.current) return;

        const rect = triggerRef.current.getBoundingClientRect();

        // Calculate base position based on tooltip position prop
        // Using getBoundingClientRect() which gives viewport coordinates
        // Since we're using position: fixed, we don't need scroll offsets
        let top, left, transform;

        if (position === "top") {
          top = rect.top;
          left = rect.left + rect.width / 2;
          transform = "translate(-50%, -100%)";
          tooltipRef.current.style.marginTop = "-8px";
          tooltipRef.current.style.marginLeft = "0";
        } 
        else if (position === "top-left") {
          top = rect.top;
          left = rect.left;
          transform = "translate(0, -100%)";
          tooltipRef.current.style.marginTop = "-8px";
          tooltipRef.current.style.marginLeft = "0";
        }
        else if (position === "bottom") {
          top = rect.bottom;
          left = rect.left + rect.width / 2;
          transform = "translate(-50%, 0)";
          tooltipRef.current.style.marginTop = "8px";
          tooltipRef.current.style.marginLeft = "0";
        }
        else if (position === "bottom-left") {
          top = rect.bottom;
          left = rect.left;
          transform = "translate(0, 0)";
          tooltipRef.current.style.marginTop = "8px";
          tooltipRef.current.style.marginLeft = "0";
        } 
        else if (position === "left") {
          top = rect.top + rect.height / 2;
          left = rect.left;
          transform = "translate(-100%, -50%)";
          tooltipRef.current.style.marginTop = "0";
          tooltipRef.current.style.marginLeft = "-8px";
        } else if (position === "right") {
          top = rect.top + rect.height / 2;
          left = rect.left + rect.width;
          transform = "translate(0, -50%)";
          tooltipRef.current.style.marginTop = "0";
          tooltipRef.current.style.marginLeft = "8px";
        }

        // Apply positioning styles (using fixed positioning relative to viewport)
        tooltipRef.current.style.left = `${left}px`;
        tooltipRef.current.style.top = `${top}px`;
        tooltipRef.current.style.transform = transform;
        tooltipRef.current.style.bottom = "auto";
        tooltipRef.current.style.right = "auto";
        // Reset any previous clamp before re-measuring below, so content
        // that got shorter (or a window resize that freed up room) isn't
        // stuck at an old constrained height.
        tooltipRef.current.style.maxHeight = "none";
        tooltipRef.current.style.overflowY = "visible";

        // position:fixed doesn't auto-clamp to the viewport - a tooltip
        // taller than the room actually available between the trigger and
        // the screen edge it opens toward (e.g. a long list opening below
        // a trigger that itself sits partway down the page) just runs past
        // that edge with no way to reach the rest. Re-measure the tooltip's
        // real rendered box (now that top/left/transform are applied) and
        // cap it only when it would actually overflow - most tooltips never
        // hit this and render at their natural height same as before.
        const viewportMargin = 12;
        if (position === "bottom" || position === "bottom-left") {
          const tooltipRect = tooltipRef.current.getBoundingClientRect();
          const overflowBy = tooltipRect.bottom - (window.innerHeight - viewportMargin);
          if (overflowBy > 0) {
            tooltipRef.current.style.maxHeight = `${tooltipRect.height - overflowBy}px`;
            tooltipRef.current.style.overflowY = "auto";
          }
        } else if (position === "top" || position === "top-left") {
          const tooltipRect = tooltipRef.current.getBoundingClientRect();
          const overflowBy = viewportMargin - tooltipRect.top;
          if (overflowBy > 0) {
            tooltipRef.current.style.maxHeight = `${tooltipRect.height - overflowBy}px`;
            tooltipRef.current.style.overflowY = "auto";
          }
        }
      };

      // Initial position calculation
      requestAnimationFrame(() => {
        updatePosition();
      });

      // Update on scroll and resize
      window.addEventListener("scroll", updatePosition, true);
      window.addEventListener("resize", updatePosition);

      return () => {
        window.removeEventListener("scroll", updatePosition, true);
        window.removeEventListener("resize", updatePosition);
      };
    }
  }, [visible, position]);

  return (
    <span
      ref={triggerRef}
      className={`${styles.tooltipWrapper} ${customClass}`}
      onMouseEnter={showTip}
      onMouseLeave={hideTip}
    >
      {children}
      {visible &&
        createPortal(
          <div
            ref={tooltipRef}
            className={`${styles.tooltip} ${customClass}`}
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              ...(zIndex !== null && { zIndex }),
              ...tooltipStyle,
            }}
          >
            {arrow && (
              <div
                className={`${styles.arrow} ${styles[`arrow_${position}`]}`}
              />
            )}
            <div className={styles.tooltipContent}>{content}</div>
          </div>,
          getOverlayRoot()
        )}
    </span>
  );
};

export default Tooltip;
